import React, { useEffect, useState } from 'react';
import TagModal from './TagModal';
import SideMenu from './SideMenu';
import SocialListening from './SocialListening';
import CreatePost from './CreatePost';
import { useGetSocialListeningQuery } from '../../../Services/instagram/Instagram';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { TextField, Button, Box, Typography, Paper, Divider, Avatar, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { FILE_UPLOAD_URL } from '../../../env';
import { toast } from 'react-toastify';
import { useCreateInstagramPostMutation, useGetInstagramPostQuery } from '../../../Services/instagram/Instagram';

// Styled components for the posting UI
const PostingContainer = styled(Paper)({
    padding: '24px',
    margin: '20px auto',
    maxWidth: '800px',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    backgroundColor: '#fff',
    border: '1px solid #dbdbdb'
});

const PostingForm = styled('form')({
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginTop: '20px'
});

const PreviewImage = styled('img')({
    maxWidth: '100%',
    maxHeight: '400px',
    borderRadius: '8px',
    objectFit: 'cover'
});

const Post = () => {
    const [activeTab, setActiveTab] = useState('posting'); // 'social' or 'posting'
    const [openTagModal, setOpenTagModal] = useState(false);
    const [openCreatePostModal, setOpenCreatePostModal] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [postContent, setPostContent] = useState('');
    const [scheduledTime, setScheduledTime] = useState('');
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imageUploadLoading,setImageUploadLoading] = useState(false)
    const { data: socialListeningData, status: socialListeningStatus, refetch: refetchSocialListening } = useGetSocialListeningQuery(
        { socialListening: "instagram" },
        { skip: activeTab !== 'social' }
    );
    const [loading, setLoading] = useState(false);
    const [uploadUrl, setUploadUrl] = useState('')
    const [instagramPostLoading,setInstagramPostLoading] = useState(false)

    const handleOpenTagModal = () => setOpenTagModal(true);
    const handleCloseTagModal = () => setOpenTagModal(false);
    const handleOpenCreatePostModal = () => setOpenCreatePostModal(true);
    const handleCloseCreatePostModal = () => setOpenCreatePostModal(false);
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setTagInput(e.target.value);
    const [createInstagramPost] = useCreateInstagramPostMutation();
    const { data: instagramPostData, status: instagramPostStatus, refetch: refetchInstagramPost } = useGetInstagramPostQuery({}, { skip: activeTab !== 'posting' });
    const [selectedChannel, setSelectedChannel] = useState<string>('instagram');
    const handleAddTag = () => {
        const trimmed = tagInput.trim();
        if (trimmed && !tags.includes(trimmed)) {
            setTags([...tags, trimmed]);
        }
        setTagInput('');
    };
    const handleDeleteTag = (tagToDelete: string) => setTags(tags.filter(tag => tag !== tagToDelete));

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInstagramPostLoading(true);
        const files = e.target.files;
        setImageUploadLoading(true)
        const fileSingle = e.target.files[0]
        if (files && files.length > 0) {
            const newFiles = Array.from(files);
            const newPreviews: string[] = [];

            newFiles.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    newPreviews.push(reader.result as string);
                    if (newPreviews.length === newFiles.length) {
                        setImagePreviews(prev => [...prev, ...newPreviews]);
                        setImageFiles(prev => [...prev, ...newFiles]);
                    }
                };
                reader.readAsDataURL(file);
                imageUpload([fileSingle]).finally(() => setInstagramPostLoading(false));
            });
        } else {
            setInstagramPostLoading(false);
            setImageUploadLoading(false)
        }
    };

    const handleRemoveImage = (index: number) => {
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
        setImageFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handlePostSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setInstagramPostLoading(true);
        // Here you would typically handle the post submission
        console.log({
            content: postContent,
            scheduledTime: scheduledTime,
            tags,
            imageFiles: imageFiles
        });


        let body: any = {
            caption: postContent
        };
        if (imageFiles && imageFiles[0]) {
            const fileType = imageFiles[0].type;
            console.log(fileType,"videos")
            if (fileType.startsWith('video/')) {
                body.video_url = uploadUrl;
                body.media_type = "REELS"
            } else {
                body.image_url = uploadUrl;
            }
        } else {
            body.image_url = uploadUrl;
        }
        try {
            const res = await createInstagramPost({ body });
            // Only show success if no error in response
            if (res?.error) {
                // RTK Query may return error in response
                let errorMsg = 'Failed to create post.';
                const err = res.error as any;
                if ('data' in err && err.data) {
                    // FetchBaseQueryError
                    if (typeof err.data === 'object') {
                        if ('message' in err.data && typeof err.data.message === 'string') {
                            errorMsg = err.data.message;
                        } else {
                            errorMsg = Object.entries(err.data)
                                .map(([field, msgs]) => `${field}: ${(Array.isArray(msgs) ? msgs.join(', ') : msgs)}`)
                                .join(' | ');
                        }
                    }
                } else if ('message' in err && typeof err.message === 'string') {
                    // SerializedError
                    errorMsg = err.message;
                }
                toast.error(errorMsg);
            } else {
                const msg = res?.data?.message || 'Post created successfully!';
                toast.success(msg);
                setPostContent('');
                setScheduledTime('');
                setImagePreviews([]);
                setImageFiles([]);
                setUploadUrl('')
                handleCloseCreatePostModal()
                refetchInstagramPost()
            }
        } catch (error: any) {
            let errorMsg = 'Failed to create post.';
            if (error && typeof error === 'object') {
                if ('data' in error && error.data) {
                    if (typeof error.data === 'object') {
                        if ('message' in error.data && typeof error.data.message === 'string') {
                            errorMsg = error.data.message;
                        } else {
                            errorMsg = Object.entries(error.data)
                                .map(([field, msgs]) => `${field}: ${(Array.isArray(msgs) ? msgs.join(', ') : msgs)}`)
                                .join(' | ');
                        }
                    }
                } else if ('message' in error && typeof error.message === 'string') {
                    errorMsg = error.message;
                }
            }
            toast.error(errorMsg);
            setUploadUrl('')
        } finally {
            setInstagramPostLoading(false);
        }
        // Reset form

    };

    // Dynamically generate starter system cards from socialListeningData
    const starterSystems = React.useMemo(() => {
        if (!socialListeningData || !Array.isArray(socialListeningData.data)) return [];
        let result = [];
        socialListeningData.data.forEach((brandObj: any) => {
            const brandName = Object.keys(brandObj)[0];
            const brandData = brandObj[brandName]?.data;
            if (Array.isArray(brandData)) {
                brandData.forEach((post: any) => {
                    result.push({
                        image: post.media_url,
                        media_type: post.media_type,
                        title: brandName,
                        description: post.caption,
                        permalink: post.permalink,
                        timestamp: post.timestamp,
                    });
                });
            }
        });
        return result;
    }, [socialListeningData]);


    const instagramPost = React.useMemo(() => {
        if (!instagramPostData || !Array.isArray(instagramPostData.posts)) return [];
        return instagramPostData.posts.map((post: any) => ({
            id: post.id,
            image: post.media_url,
            media_type: post.media_type,
            title: post.username,
            description: post.caption,
            permalink: post.permalink,
            timestamp: post.timestamp,
            tagRemoved:true
        }));
    }, [instagramPostData]);

    useEffect(() => {
        if (socialListeningStatus === "pending" || instagramPostStatus === "pending") {
            setLoading(true)
        } else {
            setLoading(false)
        }
    }, [socialListeningStatus, instagramPostStatus])


    console.log(instagramPostData, "instagramPostData")
    async function imageUpload(uploadedFiles: any) {
        if (uploadedFiles !== undefined) {
            // console.log(uploadedFiles);

            uploadedFiles.map(async (value: any, index: any) => {

                let files: any = [];
                files.push({
                    fileName: value.name,
                    mimeType: value.type,
                });

                const data = { files };
                try {
                    let response = await axios.post(FILE_UPLOAD_URL as string, data, {
                        headers: {

                            'Content-Type': 'application/json',
                        }
                    },);
                    const uploadURL = response.data[0].uploadURL;

                    // Send file to uploadURL after the url is created in s3
                    const finalresponse = await axios.put(uploadURL, uploadedFiles[0], {
                        headers: {
                            'Content-Type': value.type,
                        }
                    });
                    setUploadUrl(response.data[0].url)
                    setImageUploadLoading(false)
                    console.log(finalresponse, "image test")
                } catch (err) {
                    console.error(err);
                    setImageUploadLoading(false)
                }
            });
        }
    }
    return (
        <div style={{ display: 'flex', background: '#fff' }}>
            {/* Sidebar */}
          

            {/* Main Content */}
            {loading ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    marginLeft: 0
                }}>
                    <LoadingSpinner />
                </div>
            ) : (
                <div style={{ flex: 1, position: 'relative', padding: '10px 10px', marginLeft: 0 }}>
               
                    {activeTab === 'posting' && (
                        <>
                            <button
                                className="btn btn-primary"
                                style={{
                                    position: 'absolute',
                                    top: 30,
                                    right: 30
                                }}
                                onClick={handleOpenCreatePostModal}
                            >
                                Create post
                            </button>
                            <Modal
                                open={openCreatePostModal}
                                onClose={handleCloseCreatePostModal}
                                aria-labelledby="create-post-modal"
                                aria-describedby="modal-for-instagram-create-post"
                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, marginTop: "auto" }}
                            >

                                <CreatePost
    postContent={postContent}
    setPostContent={setPostContent}
    imagePreviews={imagePreviews}
    imageFiles={imageFiles}
    uploadUrl={uploadUrl}
    handleImageUpload={handleImageUpload}
    handleRemoveImage={handleRemoveImage}
    handlePostSubmit={handlePostSubmit}
    handleCloseCreatePostModal={handleCloseCreatePostModal}
    instagramPostLoading={instagramPostLoading}
    selectedChannel={selectedChannel}
    setSelectedChannel={setSelectedChannel}
    imageUploadLoading={imageUploadLoading}
/>

                            </Modal>
                        </>
                    )}

                

                    {activeTab === 'posting' && (
                        <div style={{ width: '100%' }}>
                            <div style={{ maxWidth: 1200, margin: '0', marginLeft: "30px" ,marginBottom:"45px"}}>
                                <div className="fw-bold" style={{ fontSize: 26, marginBottom: 3 }}>
                                    Social media post
                                  
                                </div>
                                <div style={{ color: '#6d6e7b', fontSize: 14, marginBottom: 10, marginTop: 2 }}>
                                    Create social media posts with ease. Upload content, write captions, and manage your content-all in one place.
                                </div>
                            </div>
                            <SocialListening starterSystems={instagramPost} loading={loading} />
                        </div>
                    )}
                </div>
            )}
            <TagModal
                open={openTagModal}
                onClose={handleCloseTagModal}
                tags={tags}
                tagInput={tagInput}
                onInputChange={handleInputChange}
                onAddTag={handleAddTag}
                onDeleteTag={handleDeleteTag}
                setTags={setTags}
                refetchSocialListening={refetchSocialListening}
                socialListening="instagram"
            />
        </div>
    );
};

export default Post;
